import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

const defaultAvatar = 'https://ui-avatars.com/api/?name=User&background=random&color=fff';

const CVTemplateDefault = ({ user, projects, language = 'id' }) => {
  if (!user) return null;

  const {
    email,
    photo,
    job,
    phone,
    location,
    summary,
    skills = [],
    experience = [],
    education = [],
  } = user;

  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = education.length > 0 ? 
    categorizeEducation(education) : { higher: [], k12: [] };

  return (
    <div className="cv-template avoid-break bg-white text-black p-4 sm:p-6 md:p-8 max-w-4xl mx-auto font-sans print:p-8 print:max-w-full">
      {/* Header */}
      <div className="cv-section flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 border-b pb-4 sm:pb-6 avoid-break">
        <img
          src={photo || defaultAvatar}
          alt="avatar"
          className="cv-photo w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 object-cover rounded-full border print:w-32 print:h-32"
        />
        <div className="text-center sm:text-left">
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{getFormattedUserName(user)}</h1>
          <p className="text-base sm:text-lg text-gray-700 mt-1">{job}</p>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">{email}</p>
          <p className="text-xs sm:text-sm text-gray-600">{phone}</p>
          <p className="text-xs sm:text-sm text-gray-600">{location}</p>
        </div>
      </div>

      {/* Ringkasan */}
      <section className="cv-section mt-4 sm:mt-6 avoid-break">
        <h2 className="cv-section-title text-lg sm:text-xl font-semibold border-b pb-1 mb-2">Ringkasan</h2>
        <p className="text-xs sm:text-sm leading-relaxed text-gray-800 whitespace-pre-line text-justify">{summary}</p>
      </section>

      {/* Keahlian */}
      <section className="cv-section mt-4 sm:mt-6 avoid-break">
        <h2 className="cv-section-title text-lg sm:text-xl font-semibold border-b pb-1 mb-2">{t('skills', language)}</h2>
        <ul className="flex flex-wrap gap-2 text-xs sm:text-sm text-gray-800">
          {skills.map((skill, idx) => (
            <li key={idx} className="bg-gray-200 px-2 py-1 rounded">{skill}</li>
          ))}
        </ul>
      </section>

      {/* Pengalaman */}
      <section className="cv-section mt-4 sm:mt-6 avoid-break">
        <h2 className="cv-section-title text-lg sm:text-xl font-semibold border-b pb-1 mb-2">{t('experience', language)}</h2>
        {experience.map((exp, idx) => (
          <div key={idx} className="cv-experience-item mb-3 avoid-break">
            <p className="font-semibold text-sm sm:text-base">{exp.position} - {exp.company}</p>
            <p className="text-xs sm:text-sm text-gray-700 italic">{exp.period}</p>
            <p className="text-xs sm:text-sm text-gray-800 whitespace-pre-line text-justify">{exp.description}</p>
          </div>
        ))}
      </section>

      {/* Pendidikan Tinggi */}
      {higherEducation.length > 0 && (
        <section className="cv-section mt-4 sm:mt-6 avoid-break">
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold border-b pb-1 mb-2">{t('higherEducation', language)}</h2>
          {higherEducation.map((edu, idx) => (
            <div key={idx} className="cv-education-item mb-3 avoid-break">
              <p className="font-semibold text-sm sm:text-base">{edu.degree}</p>
              <p className="text-xs sm:text-sm text-gray-700">{edu.school || edu.institution}</p>
              <p className="text-xs sm:text-sm text-gray-600 italic">{edu.year}</p>
            </div>
          ))}
        </section>
      )}

      {/* Pendidikan Formal (K-12) */}
      {k12Education.length > 0 && (
        <section className="cv-section mt-4 sm:mt-6 avoid-break">
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold border-b pb-1 mb-2">{t('formalEducation', language)}</h2>
          {k12Education.map((edu, idx) => (
            <div key={idx} className="cv-education-item mb-3 avoid-break">
              <p className="font-semibold text-sm sm:text-base">{edu.degree}</p>
              <p className="text-xs sm:text-sm text-gray-700">{edu.school || edu.institution}</p>
              <p className="text-xs sm:text-sm text-gray-600 italic">{edu.year}</p>
            </div>
          ))}
        </section>
      )}

      {/* Projek */}
      {projects?.length > 0 && (
        <section className="cv-section mt-4 sm:mt-6 avoid-break">
          <h2 className="cv-section-title text-lg sm:text-xl font-semibold border-b pb-1 mb-2">{t('projects', language)}</h2>
          {projects.map((project, idx) => (
            <div key={idx} className="cv-project-item mb-3 avoid-break">
              <p className="font-semibold text-gray-900 text-sm sm:text-base">{project.title}</p>
              <p className="text-xs sm:text-sm text-gray-800 whitespace-pre-line text-justify">{project.description}</p>
              {/* Gambar proyek DIHILANGKAN agar desain tetap rapi */}
            </div>
          ))}
        </section>
      )}
    </div>
  );
};

export default CVTemplateDefault;
